import React from 'react';
import {
  LineChart,
  BarChart3,
  TrendingUp,
  PieChart,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Sparkles,
} from 'lucide-react';

export const AdminAnalyticsView: React.FC = () => {
  const monthlyData = [
    { month: 'ม.ค. 69', collected: 495000, target: 510000, rate: 97 },
    { month: 'ก.พ. 69', collected: 490000, target: 510000, rate: 96 },
    { month: 'มี.ค. 69', collected: 505000, target: 510000, rate: 99 },
    { month: 'เม.ย. 69', collected: 488000, target: 510000, rate: 95 },
    { month: 'พ.ค. 69', collected: 502000, target: 510000, rate: 98 },
    { month: 'มิ.ย. 69', collected: 497000, target: 510000, rate: 97 },
    { month: 'ก.ค. 69', collected: 508000, target: 510000, rate: 99 },
    { month: 'ส.ค. 69', collected: 465000, target: 510000, rate: 91 },
  ];

  const maintenanceBreakdown = [
    { label: 'ไฟฟ้า & โคมไฟถนน', count: 28, pct: 35, color: 'bg-amber-500' },
    { label: 'ระบบประปา & ปั๊มน้ำ', count: 22, pct: 27, color: 'bg-blue-500' },
    { label: 'สระว่ายน้ำ & คลับเฮาส์', count: 15, pct: 19, color: 'bg-teal-500' },
    { label: 'งานสวน & ต้นไม้', count: 10, pct: 12, color: 'bg-emerald-500' },
    { label: 'อื่นๆ / จิปาถะ', count: 6, pct: 7, color: 'bg-purple-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-violet-50 text-violet-600 border border-violet-200">
            <LineChart className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              16. ศูนย์วิเคราะห์ข้อมูลและสถิตินิติบุคคล (Analytics & Business Intelligence)
            </h2>
            <p className="text-xs text-slate-500">
              วิเคราะห์ประสิทธิภาพการจัดเก็บรายได้, สถิติงานซ่อมบำรุง, ความพึงพอใจ และความปลอดภัย
            </p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">อัตราจัดเก็บค่าส่วนกลางเฉลี่ย</span>
          <p className="text-2xl font-bold text-emerald-700 font-mono">97.2%</p>
          <p className="text-[11px] text-emerald-600 font-medium">สูงกว่าเป้าหมายมาตรฐาน (+2.2%)</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">เวลาเฉลี่ยในการปิดงานซ่อม (MTTR)</span>
          <p className="text-2xl font-bold text-blue-700 font-mono">3.4 ชม.</p>
          <p className="text-[11px] text-blue-600 font-medium">รวดเร็วกว่าเกณฑ์ SLA 24 ชม.</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">ความแม่นยำลาดตระเวน รปภ.</span>
          <p className="text-2xl font-bold text-indigo-700 font-mono">100%</p>
          <p className="text-[11px] text-indigo-600 font-medium">สแกน QR ครบ 7 จุดตรวจทุกรอบ</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <span className="text-xs text-slate-500">ความพึงพอใจของลูกบ้าน</span>
          <p className="text-2xl font-bold text-amber-600 font-mono">4.9 / 5.0</p>
          <p className="text-[11px] text-amber-600 font-medium">จากแบบประเมิน 118 หลัง</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Collection Trend */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">
                แนวโน้มการจัดเก็บค่าส่วนกลางรายเดือน (ปี 2569)
              </h3>
              <p className="text-xs text-slate-500">เปรียบเทียบยอดจัดเก็บจริง vs เป้าหมายงบประมาณ</p>
            </div>
            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
              สถานะการเงินมั่นคง
            </span>
          </div>

          <div className="space-y-3 pt-2">
            {monthlyData.map((m, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between text-xs font-mono">
                  <span className="font-sans font-semibold text-slate-800">{m.month}</span>
                  <span className="text-slate-600">
                    ฿{m.collected.toLocaleString()} ({m.rate}%)
                  </span>
                </div>
                <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${m.rate}%` }}
                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Maintenance Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-slate-900 text-sm">สัดส่วนงานแจ้งซ่อมบำรุง</h3>
            <p className="text-xs text-slate-500">จำแนกตามระบบวิศวกรรม</p>

            <div className="space-y-3 pt-4">
              {maintenanceBreakdown.map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-700">{item.label}</span>
                    <span className="font-bold font-mono text-slate-900">{item.pct}% ({item.count} งาน)</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      style={{ width: `${item.pct}%` }}
                      className={`h-full ${item.color} rounded-full`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-500 border border-slate-200">
            💡 ระบบไฟถนนและปั๊มน้ำเป็นหมวดหมู่หลักที่มีการบำรุงรักษาเชิงป้องกันสม่ำเสมอ
          </div>
        </div>
      </div>
    </div>
  );
};
